import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import shallow from 'zustand/shallow'

import PerText from '../Text'
import PerFlexRow from '../FlexRow'
import PerButton from '../Button'
import PerSpacer from '../Spacer'
import { useForecastModalStore, transformPyramidForecastData } from '.'
import { Country, PyramidForecastInput } from '../../../types'
import { savePyramidForecast } from '../../../graphql/pyramids/hooks/useSavePyramidForecast'
import { usePyramidsStore } from '../../../modules/pyramids/PyramidsStore'
import { useEditForecastPermission } from '../../../modules/pyramids/usePermission'
import { ForeacastModalTabs } from './types'
import { showNotification } from '../Notification/notification'

interface IForecastModalSaveButton {
  companyId: string
  country: Country
}

export const ForecastModalSaveButton: FC<IForecastModalSaveButton> = ({ companyId, country }) => {
  const { t } = useTranslation()
  const [isProcessing, toggleProcessing] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<string>('')

  const [employeeeData, hiringsTableForm, employeeTableFormValues, deletedHirings, activeTab, toggleActiveTab, toggleModal] =
    useForecastModalStore(
      (state) => [
        state.pyramidForecastResponse,
        state.hiringsTableForm,
        state.employeeTableFormValues,
        state.deletedHirings,
        state.activeTab,
        state.toggleActiveTab,
        state.toggleModal,
      ],
      shallow,
    )

  const hasPermission = useEditForecastPermission(companyId)

  const [forecastDate] = usePyramidsStore((state) => [state.forecastDate], shallow)

  const saveForecast = async (companyId: string, pyramidForecasts: PyramidForecastInput[]) => {
    const { errors } = await savePyramidForecast(companyId, country, pyramidForecasts, forecastDate.year, forecastDate.month)
    if (!!errors) {
      setErrorMessage(t('pyramidsView.forecastModal.errors.response'))
    } else {
      showNotification({
        type: 'success',
        message: t('pyramidsView.forecastModal.notifications.success.title'),
        description: t('pyramidsView.forecastModal.notifications.success.description'),
      })
      toggleModal(false)
    }
  }

  const hasErrors = Object.keys(employeeTableFormValues).some((employeeId: string) => !!employeeTableFormValues[employeeId].hasErrors)

  const handleSubmit = async () => {
    setErrorMessage('')
    toggleProcessing(true)

    const newHirings = !!hiringsTableForm && !!hiringsTableForm.getValues ? hiringsTableForm.getValues().hirings : []
    const pyramidForecasts = transformPyramidForecastData(
      companyId,
      country,
      employeeeData,
      newHirings,
      employeeTableFormValues,
      deletedHirings,
    )

    if (!!hasErrors) {
      if (activeTab !== ForeacastModalTabs.Employees) {
        toggleActiveTab(ForeacastModalTabs.Employees)
      }
      setErrorMessage(t('pyramidsView.forecastModal.errors.required'))
    } else if (!!hiringsTableForm && !!hiringsTableForm.trigger) {
      await hiringsTableForm.trigger()
      if (!!Object.keys(hiringsTableForm.formState.errors).length) {
        if (activeTab !== ForeacastModalTabs.Hirings) {
          toggleActiveTab(ForeacastModalTabs.Hirings)
        }
        setErrorMessage(t('pyramidsView.forecastModal.errors.required'))
      } else {
        if (!!pyramidForecasts.length) {
          await saveForecast(companyId, pyramidForecasts)
        }
      }
    } else {
      if (!!pyramidForecasts.length) {
        await saveForecast(companyId, pyramidForecasts)
      }
    }

    toggleProcessing(false)
  }

  return (
    <PerFlexRow align="middle">
      {!!errorMessage && (
        <PerText appearance="info" color="error">
          {errorMessage}
        </PerText>
      )}
      <PerSpacer ml="xxxs" mr="xxxs" />
      <PerButton
        text={t('global.button.save')}
        color="success"
        before="check_stroke"
        variant="pastel"
        type="button"
        isProcessing={{ active: isProcessing }}
        onClick={handleSubmit}
        disabled={!hasPermission}
        data-testid="forecast-save-btn"
      />
    </PerFlexRow>
  )
}
